import {
	CANONICAL_SCHEMAS,
	type FileConfigFieldError,
	getCanonicalSchema,
	IMPORT_TRANSFORM_CATALOGUE,
	ImportFormatType,
	LOGICAL_FILE_TYPES,
	type LogicalFileType,
} from '@today/types'
import { FieldShell } from '@today/ui-components'
import {
	FILE_TYPE_LABELS,
	FieldMappingEditor,
	FixedWidthRuler,
	type MappingTarget,
	ProcessingStep,
	SourceFieldsEditor,
} from '@today/ui-domain'
import {
	type DesignerState,
	editSourceField,
	missingProcessing,
	selectLogicalFile,
	selectSchema,
	setMapping,
	setNaming,
	setProcessing,
	setRecordRules,
	sourceRowsOf,
	toggleRulerColumn,
} from './designer-state'

// The designer's editing steps (TO-400 Happy path 1–5). Each renders pure
// ui-domain views over the designer state and reports edits as state updates.

const INPUT_CLASS =
	'h-10 w-full rounded-ds-md border border-ds-border bg-ds-surface px-3 text-ds-base text-ds-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring disabled:opacity-50'

export interface StepProps {
	state: DesignerState
	update: (fn: (state: DesignerState) => DesignerState) => void
}

/** The contract violations of one step, next to the controls that caused them. */
export function Violations({ fields }: { fields: readonly FileConfigFieldError[] }) {
	if (fields.length === 0) return null
	return (
		<ul role="alert" aria-label="Fields that need attention" className="text-ds-sm text-ds-danger">
			{fields.map((f) => (
				<li key={`${f.path}:${f.code}`}>
					{f.path}: {f.code}
				</li>
			))}
		</ul>
	)
}

const schemaId = (s: { name: string; version: number }) => `${s.name}@${s.version}`

function SchemaSelect({ state, update }: StepProps) {
	return (
		<FieldShell label="Canonical target">
			<select
				className={INPUT_CLASS}
				value={schemaId(state.canonicalSchema)}
				onChange={(e) => {
					const schema = CANONICAL_SCHEMAS.find((s) => schemaId(s) === e.target.value)
					if (schema) update((s) => selectSchema(s, { name: schema.name, version: schema.version }))
				}}
			>
				{CANONICAL_SCHEMAS.map((s) => (
					<option key={schemaId(s)} value={schemaId(s)}>
						{schemaId(s)}
					</option>
				))}
			</select>
		</FieldShell>
	)
}

export function TargetStep({ state, update }: StepProps) {
	return (
		<div className="flex flex-col gap-3">
			<FieldShell label="Logical file">
				<select
					className={INPUT_CLASS}
					value={state.logicalFile}
					onChange={(e) => update((s) => selectLogicalFile(s, e.target.value as LogicalFileType))}
				>
					{LOGICAL_FILE_TYPES.map((f) => (
						<option key={f} value={f}>
							{FILE_TYPE_LABELS[f]}
						</option>
					))}
				</select>
			</FieldShell>
			<SchemaSelect {...{ state, update }} />
			<FieldShell label="Definition key">
				<input
					className={INPUT_CLASS}
					value={state.key}
					disabled={state.base !== null}
					onChange={(e) => {
						const key = e.target.value
						update((s) => ({ ...s, key }))
					}}
				/>
			</FieldShell>
			<FieldShell label="File name pattern (regular expression)">
				<input
					className={INPUT_CLASS}
					value={state.contract.file.naming.pattern}
					onChange={(e) => {
						const pattern = e.target.value
						update((s) => setNaming(s, pattern))
					}}
				/>
			</FieldShell>
		</div>
	)
}

export function FieldsStep({ state, update }: StepProps) {
	const { format } = state.contract.file
	return (
		<div className="flex flex-col gap-4">
			{format.type === ImportFormatType.FIXED_WIDTH && state.lineLength > 0 && (
				<FixedWidthRuler
					lines={state.inspection?.lines ?? []}
					lineLength={state.lineLength}
					boundaries={state.boundaries}
					onToggle={(column) => update((s) => toggleRulerColumn(s, column))}
				/>
			)}
			<SourceFieldsEditor
				fields={sourceRowsOf(state)}
				onChange={(i, edit) => update((s) => editSourceField(s, i, edit))}
			/>
		</div>
	)
}

const targetsOf = (state: DesignerState): MappingTarget[] =>
	(getCanonicalSchema(state.canonicalSchema.name, state.canonicalSchema.version)?.fields ?? [])
		.filter((f) => f.localized !== true)
		.map((f) => ({ name: f.name, type: f.type, required: f.required }))

export function MappingStep({ state, update }: StepProps) {
	return (
		<FieldMappingEditor
			targets={targetsOf(state)}
			sources={state.contract.file.format.fields.map((f) => f.name)}
			mapping={state.contract.mapping.fields}
			rules={state.contract.validation.record}
			catalogue={IMPORT_TRANSFORM_CATALOGUE}
			onMappingChange={(fields) => update((s) => setMapping(s, fields))}
			onRulesChange={(rules) => update((s) => setRecordRules(s, rules))}
		/>
	)
}

export function ProcessingStepPanel({ state, update }: StepProps) {
	return (
		<ProcessingStep
			processing={state.contract.processing}
			keyCandidates={state.contract.mapping.fields.map((m) => m.target)}
			missing={missingProcessing(state)}
			onChange={(processing) => update((s) => setProcessing(s, processing))}
		/>
	)
}
